import React from "react";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import Radio from "common/Radio/Radio";
import styled from "styled-components";
import { OnboardingStyles } from "features/onboarding/common/styles";
import Col from "antd/es/col";
import Row from "antd/es/row";
import Tooltip from "antd/es/tooltip";
import {
  AccountData,
  AccountsListProps,
} from "@shared/features/onboarding/types";
import useAccountsList from "@shared/features/onboarding/hooks/useAccountsList";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import AccountRow from "features/onboarding/overview/accountsList/AccountRow";
import AccountMainCoins from "features/onboarding/overview/accountsList/AccountMainCoins";
import useMedia from "utils/hooks/useMedia";
import { LARGE_MOBILE } from "common/constants";
import {
  MAX_ACCOUNTS_ROWS,
  RADIO_SIZE,
} from "@shared/features/onboarding/constants";
import Link from "common/Typography/Link";
import Modal from "common/Modal/Modal";
import Button from "common/Buttons/Button";
import Card from "common/Boxes/Card";
import { useBottomNavigator } from "utils/hooks/misc";
import { openMessage } from "utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { ONBOARDING_ACCOUNT_RADIO_ID } from "testIds";
import useQuery from "utils/hooks/useQuery";
import { useSelector } from "react-redux";
import { selectWorkspace } from "@shared/slices/common.slice";
import { WorkspaceType } from "@shared/types";

const AccountsList = ({
  groupData,
  symbol,
  isBot,
  isOnboarding,
  preferredExchange,
}: AccountsListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const isMobile = useMedia([`(max-width: ${LARGE_MOBILE})`], [true], false);
  const bottomNavigatorData = useBottomNavigator();
  const query = useQuery();
  const isDemo = useSelector(selectWorkspace) === WorkspaceType.DEMO;
  const preferredAccountId =
    isOnboarding && isDemo
      ? Number(query.get("account-id")) || undefined
      : undefined;

  const {
    selectedAccountId,
    accountsListData,
    sortedAccountsData,
    isSingleAccount,
    isRadioDisabled,
    coin,
    spans,
    isRadio,
    getAccountRowData,
    getTooltipErrorMessage,
    showMore,
    isAccountsModalVisible,
    setIsAccountsModalVisible,
    closeAccountsListModal,
    onClickUseSelectedAccount,
    accounts,
  } = useAccountsList(
    groupData,
    symbol,
    isBot,
    preferredExchange,
    preferredAccountId,
    !!isOnboarding && !isBot
  );

  const getAccountRow = (account: AccountData) => {
    const props = getAccountRowData(account);
    return (
      <div style={{ padding: "12px 0", width: "100%" }}>
        <AccountRow
          account={account}
          currency={isBot ? coin : "USD"}
          disabled={props.disabled}
          total={props.total}
          available={props.available}
          isBot={isBot}
          totalSignals={account.signals}
          spans={spans}
          isOnboarding={isOnboarding}
        />
        {props.showMainCoins && (
          <AccountMainCoins account={account} spans={spans} isRadio={isRadio} />
        )}
      </div>
    );
  };

  const getRadio = (account: AccountData) => (
    <OnboardingStyles.Radio
      data-testid={`${ONBOARDING_ACCOUNT_RADIO_ID}-${account.id}`}
      value={account.id}
      key={account.id}
      disabled={isRadioDisabled(account)}
    >
      {getAccountRow(account)}
    </OnboardingStyles.Radio>
  );

  const getAccountsList = (showAll?: boolean) => {
    const accountsList = showAll
      ? accounts
      : accounts?.slice(0, MAX_ACCOUNTS_ROWS);

    return accountsList ? (
      <Radio.Group
        value={accountsListData.value}
        onChange={(event) => accountsListData.onChange(event.target.value)}
        style={accountsListData.style}
        size={isMobile ? "small" : "middle"}
      >
        {accountsList.map((account) => {
          const isDisabled = isRadioDisabled(account);
          const errorMessage = isDisabled
            ? getTooltipErrorMessage(account)
            : undefined;
          const showTooltip =
            isDisabled && !bottomNavigatorData.baseShouldDisplay;
          let content = getRadio(account);
          if (showTooltip) {
            content = (
              <StyledTooltip title={errorMessage} placement="topLeft">
                {content}
              </StyledTooltip>
            );
          }
          return (
            <AccountWrapper
              key={account.id}
              onClick={
                errorMessage && !showTooltip
                  ? () => {
                      openMessage(errorMessage, "warning");
                    }
                  : undefined
              }
            >
              {content}
            </AccountWrapper>
          );
        })}
      </Radio.Group>
    ) : undefined;
  };

  const columnTitles = (
    <Row style={{ marginBottom: 8 }}>
      <Col span={spans[0]}>
        <PrimaryTitle>
          {isOnboarding
            ? t("accounts_list.exchange")
            : t("accounts_list.account")}
        </PrimaryTitle>
      </Col>
      {!isBot && (
        <StyledCol span={spans[1]} $isRadio={isRadio}>
          <SignalsTitleWrapper $isMobile={isMobile}>
            <PrimaryTitle $isMobile={isMobile}>
              {t("accounts_list.signals")}
            </PrimaryTitle>
            <SecondaryTitle>{t("accounts_list.thirty_days")}</SecondaryTitle>
          </SignalsTitleWrapper>
        </StyledCol>
      )}
      <Col
        span={spans[isBot ? 1 : 2]}
        style={{ paddingLeft: isRadio && !isBot ? RADIO_SIZE : 0 }}
      >
        <TotalTitleWrapper $isMobile={isMobile}>
          <PrimaryTitle $isMobile={isMobile}>
            {t("accounts_list.total")} {isBot && coin}
          </PrimaryTitle>
          <SecondaryTitle>{t("accounts_list.available")}</SecondaryTitle>
        </TotalTitleWrapper>
      </Col>
    </Row>
  );

  const singleAccountRow = sortedAccountsData?.length ? (
    <AccountWrapper>{getAccountRow(sortedAccountsData[0])}</AccountWrapper>
  ) : undefined;

  const showMoreButton = (
    <Link
      as="div"
      onClick={() => setIsAccountsModalVisible(true)}
      style={{ marginTop: 8 }}
    >
      {t("accounts_list.show_more")}
    </Link>
  );

  const allAccountsModal = (
    <Modal
      open={isAccountsModalVisible}
      onCancel={closeAccountsListModal}
      styles={{
        body: {
          padding: isMobile ? "24px 12px" : "32px",
        },
      }}
      width={600}
      footer={false}
      centered={true}
    >
      <OnboardingStyles.ModalTitle>
        {t("accounts_list.select_account")}
      </OnboardingStyles.ModalTitle>
      <StyledCard style={{ marginTop: 28 }}>
        {columnTitles}
        {getAccountsList(true)}
        <StyledButton type="primary" onClick={onClickUseSelectedAccount}>
          {t("accounts_list.use_selected_account")}
        </StyledButton>
      </StyledCard>
    </Modal>
  );

  return (
    <LoadingSpinnerOverlay
      isLoading={!sortedAccountsData}
      hideContentWhileLoading={true}
    >
      {columnTitles}
      {isSingleAccount ? singleAccountRow : getAccountsList()}
      {showMore && showMoreButton}
      {allAccountsModal}
    </LoadingSpinnerOverlay>
  );
};

export default AccountsList;

const AccountWrapper = styled(Flex)`
  width: 100%;
  border-bottom: ${(p) => p.theme.borders.lightest};
`;
const StyledTooltip = styled(Tooltip)`
  &.ant-tooltip-disabled-compatible-wrapper {
    width: 100%;
  }
`;
const PrimaryTitle = styled(OnboardingStyles.OverviewTitle)<{
  $isMobile?: boolean;
}>`
  margin: ${(p) => (p.$isMobile ? 0 : "0 4px 0 0")};
  && {
    color: ${(p) => p.theme.colors.secondaryText};
    font-size: 12.5px;
    line-height: 1.4;
    font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
    white-space: nowrap;
  }
`;
const SecondaryTitle = styled(Text).attrs({ type: "secondary" })`
  font-size: 12px;
  white-space: nowrap;
`;
const StyledCol = styled(Col)<{ $isRadio?: boolean }>`
  ${(p) =>
    p.$isRadio &&
    `
    transform: translate(${RADIO_SIZE}px, 0);

  `}
`;
const SignalsTitleWrapper = styled(Flex)<{ $isMobile: boolean }>`
  justify-content: start;
  align-items: ${(p) => (p.$isMobile ? "start" : "center")};
  flex-direction: ${(p) => (p.$isMobile ? "column" : "row")};
`;
const TotalTitleWrapper = styled(Flex)<{ $isMobile: boolean }>`
  justify-content: end;
  align-items: ${(p) => (p.$isMobile ? "end" : "center")};
  flex-direction: ${(p) => (p.$isMobile ? "column" : "row")};
`;
const StyledButton = styled(Button)`
  width: 100%;
  margin-top: 28px;
`;
const StyledCard = styled(Card)`
  background: ${(p) => p.theme.colors.secondaryCardBgColor};
`;
